/* @flow */

import * as React from 'react';
import { connect } from 'react-redux';

import type { BasicFunction, KeyValuePair } from '../../helpers/jsHelpers/types';
import type { ChannelMap } from '../../libs/netgemLibrary/v8/types/Channel';
import type { NETGEM_API_V8_METADATA_PROGRAM, NETGEM_API_V8_METADATA_SERIES } from '../../libs/netgemLibrary/v8/types/MetadataProgram';
import type { NETGEM_API_V8_METADATA_SCHEDULE_LOCATION } from '../../libs/netgemLibrary/v8/types/MetadataSchedule';
import type { NETGEM_API_V8_URL_DEFINITION } from '../../libs/netgemLibrary/v8/types/NtgVideoFeed';
import type { NETGEM_API_V8_RIGHTS } from '../../libs/netgemLibrary/v8/types/Rights';
import type { CombinedReducers } from '../../redux/reducers';
import type { Dispatch } from '../../redux/types/types';
import type { CARD_DATA_MODAL_TYPE } from '../modal/cardModal/CardModalConstsAndTypes';

import { channelHasKeepFromReplay, channelIsRecordable } from '../../helpers/channel/helper';
import { logError, showDebug } from '../../helpers/debug/debug';
import Localizer from '../../helpers/localization';
import { Messenger } from '../../helpers/messenger';
import { getEpisodeAndSeriesRecordStatus } from '../../helpers/npvr/recording';
import { RecordButtonActionKind } from '../../helpers/npvr/Types';
import { PendingOperationKind, clearPendingOperation, getPendingOperation } from '../../helpers/rights/pendingOperations';
import { isRecordGranted } from '../../helpers/rights/record';
import { BroadcastStatus } from '../../helpers/ui/location/Format';
import { Theme, type ThemeType } from '../../helpers/ui/theme';
import { getItemType } from '../../libs/netgemLibrary/v8/helpers/Item';
import { ItemLocationKind, ItemType, type NETGEM_API_V8_FEED_ITEM, type NETGEM_API_V8_ITEM_LOCATION, type NetgemApiV8ItemLocation } from '../../libs/netgemLibrary/v8/types/FeedItem';
import { type NETGEM_RECORDINGS_MAP, type NETGEM_SCHEDULED_RECORDINGS_MAP, ScheduledRecordingsKind, type ScheduledRecordingsKindType } from '../../libs/netgemLibrary/v8/types/Npvr';
import { TileConfigTileType } from '../../libs/netgemLibrary/v8/types/WidgetConfig';
import { FEATURE_NPVR, FEATURE_SUBSCRIPTION } from '../../redux/appConf/constants';
import { RegistrationType } from '../../redux/appRegistration/types/types';
import { hideModal } from '../../redux/modal/actions';
import { type RECORD_ITEM_TYPE, startRecord, stopRecord } from '../../redux/netgemApi/actions/v8/recorder';
import ButtonFX from '../buttons/ButtonFX';
import { HeightKind, WidthKind } from '../buttons/types';
import { PictoRecordingStart, PictoRecordingStop } from '../pictos/Element';

export enum DisplayMode {
  Button,
  Picto,
}

export enum ContextKind {
  Card,
  Player,
  Tile,
  TVEpisode,
}

type ReduxRecordButtonReducerStateType = {
  +channels: ChannelMap,
  +commercialOffers: ?KeyValuePair<Array<string>>,
  +defaultRights: ?NETGEM_API_V8_RIGHTS,
  +isDebugModeEnabled: boolean,
  +isNpvrEnabled: boolean,
  +isRegisteredAsGuest: boolean,
  +isSubscriptionFeatureEnabled: boolean,
  +npvrRecordingsFuture: NETGEM_RECORDINGS_MAP,
  +npvrRecordingsList: NETGEM_RECORDINGS_MAP,
  +npvrScheduledRecordingsList: NETGEM_SCHEDULED_RECORDINGS_MAP,
  +userRights: Array<string> | null,
};

type ReduxRecordButtonDispatchToPropsType = {
  +localHideModal: BasicFunction,
  +localStartRecord: (recordItem: RECORD_ITEM_TYPE, isDebugModeEnabled: boolean) => void,
  +localStopRecord: (recordItem: RECORD_ITEM_TYPE) => void,
};

type DefaultProps = {
  +cardDataUrlDefinition?: NETGEM_API_V8_URL_DEFINITION,
  +isDisabled?: boolean,
  +kind?: ScheduledRecordingsKindType,
  +theme?: ThemeType,
};

// If not defined, kind will be deduced from the item's metadata
type RecordButtonPropType = {
  ...DefaultProps,
  +broadcastStatus: BroadcastStatus,
  +context: ContextKind,
  +displayMode: DisplayMode,
  +item: NETGEM_API_V8_FEED_ITEM,
  +location: NETGEM_API_V8_METADATA_SCHEDULE_LOCATION | null,
  +previewCatchupScheduledEventId: ?string,
  +programMetadata: NETGEM_API_V8_METADATA_PROGRAM | null,
  +seriesMetadata: NETGEM_API_V8_METADATA_SERIES | null,
  +tileType: TileConfigTileType,
};

type CompleteRecordButtonPropType = {
  ...RecordButtonPropType,
  ...ReduxRecordButtonReducerStateType,
  ...ReduxRecordButtonDispatchToPropsType,
};

type RecordButtonStateType = {
  actionType: RecordButtonActionKind,
  hasScheduledRecording: boolean,
  hasSeriesScheduledRecording: boolean,
  isWaitingForDeleteConfirmation: boolean,
  isWaitingForRecordConfirmation: boolean,
};

const InitialState = Object.freeze({
  actionType: RecordButtonActionKind.None,
  hasScheduledRecording: false,
  hasSeriesScheduledRecording: false,
  isWaitingForDeleteConfirmation: false,
  isWaitingForRecordConfirmation: false,
});

class RecordButtonView extends React.PureComponent<CompleteRecordButtonPropType, RecordButtonStateType> {
  deleteFinishedEventName: ?string;
  recordFinishedEventName: ?string;

  static defaultProps: DefaultProps = {
    cardDataUrlDefinition: undefined,
    isDisabled: false,
    kind: undefined,
    theme: Theme.Dark,
  };

  constructor(props: CompleteRecordButtonPropType) {
    super(props);

    this.deleteFinishedEventName = null;
    this.recordFinishedEventName = null;

    this.state = { ...InitialState };
  }

  componentDidMount() {
    this.refreshState();
  }

  componentDidUpdate(prevProps: CompleteRecordButtonPropType) {
    const {
      broadcastStatus,
      item: { selectedProgramId },
      location,
      npvrRecordingsFuture,
      npvrRecordingsList,
      npvrScheduledRecordingsList,
      previewCatchupScheduledEventId,
    } = this.props;
    const {
      broadcastStatus: prevBroadcastStatus,
      item: { selectedProgramId: prevSelectedProgramId },
      location: prevLocation,
      npvrRecordingsFuture: prevNpvrRecordingsFuture,
      npvrRecordingsList: prevNpvrRecordingsList,
      npvrScheduledRecordingsList: prevNpvrScheduledRecordingsList,
      previewCatchupScheduledEventId: prevPreviewCatchupScheduledEventId,
    } = prevProps;

    if (location && location !== prevLocation) {
      this.checkPendingRecording();
    }

    if (
      selectedProgramId !== prevSelectedProgramId ||
      broadcastStatus !== prevBroadcastStatus ||
      previewCatchupScheduledEventId !== prevPreviewCatchupScheduledEventId ||
      npvrRecordingsFuture !== prevNpvrRecordingsFuture ||
      npvrRecordingsList !== prevNpvrRecordingsList ||
      npvrScheduledRecordingsList !== prevNpvrScheduledRecordingsList
    ) {
      this.refreshState();
    }
  }

  componentWillUnmount() {
    const { deleteFinishedEventName, recordFinishedEventName } = this;

    if (deleteFinishedEventName) {
      Messenger.off(deleteFinishedEventName, this.stopWaitingDeleteConfirmation);
      this.deleteFinishedEventName = null;
    }

    if (recordFinishedEventName) {
      Messenger.off(recordFinishedEventName, this.stopWaitingRecordConfirmation);
      this.recordFinishedEventName = null;
    }
  }

  showDebugInfo = () => {
    const { props, state } = this;

    showDebug('Record Button', {
      props,
      propsFields: ['cardDataUrlDefinition', 'broadcastStatus', 'displayMode', 'item', 'kind', 'location', 'previewCatchupScheduledEventId', 'programMetadata', 'seriesMetadata', 'theme', 'tileType'],
      state,
      stateFields: ['actionType', 'hasScheduledRecording', 'hasSeriesScheduledRecording', 'isWaitingForDeleteConfirmation', 'isWaitingForRecordConfirmation'],
    });
  };

  isRecordable = (
    kind?: ScheduledRecordingsKindType,
    locType: ?NetgemApiV8ItemLocation,
    selectedLocation: NETGEM_API_V8_ITEM_LOCATION,
    broadcastStatus: BroadcastStatus,
    hasRecording: boolean,
    hasScheduledRecording: boolean,
    hasSeriesScheduledRecording: boolean,
    previewCatchupScheduledEventId: ?string,
    isChannelRecordable: boolean,
    hasChannelKeepFromReplay: boolean,
  ): boolean => {
    if (kind === ScheduledRecordingsKind.KeepFromReplay && hasChannelKeepFromReplay && locType === ItemLocationKind.Catchup && broadcastStatus === BroadcastStatus.Past) {
      // Catchup with keep from replay enabled (keep from replay has been checked before)
      return true;
    }

    if (!isChannelRecordable) {
      // Channel not recordable
      return false;
    }

    if (kind === ScheduledRecordingsKind.Series) {
      // Series
      return true;
    }

    if (!selectedLocation || hasRecording || hasScheduledRecording || hasSeriesScheduledRecording) {
      // No location or (scheduled) recording already existing
      return false;
    }

    if (
      locType === ItemLocationKind.ScheduledEvent &&
      (broadcastStatus === BroadcastStatus.Live || broadcastStatus === BroadcastStatus.Future || (broadcastStatus === BroadcastStatus.Preview && previewCatchupScheduledEventId))
    ) {
      // Scheduled event with right broadcast status
      return true;
    }

    // All other cases
    return false;
  };

  refreshState = () => {
    const {
      broadcastStatus,
      channels,
      isNpvrEnabled,
      item,
      item: {
        locType,
        selectedLocation,
        selectedLocation: { channelId },
        selectedProgramId,
      },
      kind,
      npvrRecordingsFuture,
      npvrRecordingsList,
      npvrScheduledRecordingsList,
      previewCatchupScheduledEventId,
      seriesMetadata,
    } = this.props;

    let actionType: RecordButtonActionKind = RecordButtonActionKind.None;
    let hasScheduledRecording = false;
    let hasSeriesScheduledRecording = false;

    const isChannelRecordable = isNpvrEnabled && channelIsRecordable(channels, channelId);
    const hasChannelKeepFromReplay = channelHasKeepFromReplay(channels, channelId);

    if (isChannelRecordable || hasChannelKeepFromReplay) {
      const status = getEpisodeAndSeriesRecordStatus(
        selectedProgramId,
        seriesMetadata?.id,
        item,
        npvrRecordingsList,
        npvrRecordingsFuture,
        npvrScheduledRecordingsList,
        previewCatchupScheduledEventId,
      );
      const { hasRecording } = status;
      ({ hasScheduledRecording, hasSeriesScheduledRecording } = status);

      const isDeletable =
        (kind === ScheduledRecordingsKind.Series && hasSeriesScheduledRecording) ||
        ((locType === ItemLocationKind.ScheduledEvent || locType === ItemLocationKind.Recording) && (hasRecording || hasScheduledRecording || hasSeriesScheduledRecording)) ||
        (kind === ScheduledRecordingsKind.KeepFromReplay && hasRecording);

      if (isDeletable && (kind !== ScheduledRecordingsKind.Series || hasSeriesScheduledRecording)) {
        actionType = RecordButtonActionKind.Delete;
      } else if (
        this.isRecordable(
          kind,
          locType,
          selectedLocation,
          broadcastStatus,
          hasRecording,
          hasScheduledRecording,
          hasSeriesScheduledRecording,
          previewCatchupScheduledEventId,
          isChannelRecordable,
          hasChannelKeepFromReplay,
        )
      ) {
        actionType = RecordButtonActionKind.Record;
      }
    }

    this.setState(
      {
        actionType,
        hasScheduledRecording,
        hasSeriesScheduledRecording,
      },
      this.checkPendingRecording,
    );
  };

  checkPendingRecording = () => {
    const {
      isRegisteredAsGuest,
      item: { id },
      location,
    } = this.props;

    const pendingOperation = getPendingOperation();

    if (isRegisteredAsGuest || !pendingOperation?.cardData) {
      // User not logged in or no pending operation
      return;
    }

    const {
      cardData: {
        item: { id: targetId },
        pendingOperation: operation,
        recordKind,
      },
    } = pendingOperation;
    if (operation?.operationType !== PendingOperationKind.Record) {
      // Wrong operation type: ignore
      return;
    }

    if (!location) {
      // Too early: location is not defined yet
      return;
    }

    if (recordKind === ScheduledRecordingsKind.Single && id !== targetId) {
      return;
    }

    clearPendingOperation();

    // If recordKind is 'Series' or 'undefined' or 'Single' and program Id matches, starts recording
    this.startRecording(true, recordKind);
  };

  stopWaitingRecordConfirmation = () => {
    const { recordFinishedEventName } = this;

    if (recordFinishedEventName) {
      this.recordFinishedEventName = null;
    }

    this.setState({ isWaitingForRecordConfirmation: false });
  };

  stopWaitingDeleteConfirmation = () => {
    const { deleteFinishedEventName } = this;

    if (deleteFinishedEventName) {
      this.deleteFinishedEventName = null;
    }

    this.setState({ isWaitingForDeleteConfirmation: false });
  };

  handleStartRecordingButtonOnClick = (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>) => {
    const { isDebugModeEnabled } = this.props;
    const { altKey, ctrlKey } = event;

    event.preventDefault();
    event.stopPropagation();

    if (isDebugModeEnabled && (ctrlKey || altKey)) {
      this.showDebugInfo();
      return;
    }

    this.startRecording(false);
  };

  getPendingRecordKind = (): ?ScheduledRecordingsKindType => {
    const {
      item: { id },
      kind,
    } = this.props;

    if (kind !== undefined) {
      // Record kind is known (Single, Series, KeepFromReplay)
      return kind;
    }

    if (getItemType(id) === ItemType.Program) {
      // Single record button on card
      return ScheduledRecordingsKind.Single;
    }

    // Record button on series tile: user will be asked later
    return undefined;
  };

  startRecording = (isPendingOperation?: boolean, pendingRecordKind: ?ScheduledRecordingsKindType): void => {
    const {
      broadcastStatus,
      cardDataUrlDefinition,
      channels,
      commercialOffers,
      defaultRights,
      isDebugModeEnabled,
      isRegisteredAsGuest,
      isSubscriptionFeatureEnabled,
      item,
      item: {
        locType,
        selectedLocation: { id },
      },
      kind,
      localHideModal,
      localStartRecord,
      location,
      previewCatchupScheduledEventId,
      programMetadata,
      seriesMetadata,
      tileType,
      userRights,
    } = this.props;

    if (!programMetadata || !location) {
      return;
    }

    const recordKind = isPendingOperation && pendingRecordKind === undefined ? undefined : this.getPendingRecordKind();

    const cardData: CARD_DATA_MODAL_TYPE = {
      item,
      programMetadata,
      recordKind,
      seriesMetadata,
      tileType,
      urlDefinition: cardDataUrlDefinition,
    };

    if (!isRecordGranted(isRegisteredAsGuest, isSubscriptionFeatureEnabled, cardData, location, channels, commercialOffers, defaultRights, userRights)) {
      // Record denied: log-in or subscribe page has been requested
      localHideModal();
      return;
    }

    this.recordFinishedEventName = `RECORD_FINISHED_TILE_${id ?? 'no-location-id'}`;
    const { recordFinishedEventName: finishedOperationEventName } = this;
    Messenger.once(this.recordFinishedEventName, this.stopWaitingRecordConfirmation);

    // Keep from replay case
    const catchupId = locType === ItemLocationKind.Catchup ? id : undefined;

    this.setState({ isWaitingForRecordConfirmation: true }, () =>
      localStartRecord(
        {
          broadcastStatus,
          catchupId,
          finishedOperationEventName,
          item,
          kind: isPendingOperation ? pendingRecordKind : kind,
          previewCatchupScheduledEventId,
          programMetadata,
          seriesMetadata,
        },
        isDebugModeEnabled,
      ),
    );
  };

  handleStopRecordingButtonOnClick = (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>): void => {
    const {
      broadcastStatus,
      isDebugModeEnabled,
      item,
      item: {
        selectedLocation: { id },
      },
      kind,
      localStopRecord,
      previewCatchupScheduledEventId,
      programMetadata,
      seriesMetadata,
    } = this.props;
    const { hasSeriesScheduledRecording } = this.state;
    const { altKey, ctrlKey } = event;

    event.preventDefault();
    event.stopPropagation();

    if (isDebugModeEnabled && (ctrlKey || altKey)) {
      this.showDebugInfo();
      return;
    }

    if (!programMetadata) {
      return;
    }

    this.deleteFinishedEventName = `DELETE_FINISHED_TILE_${id ?? 'no-location-id'}`;
    const { deleteFinishedEventName: finishedOperationEventName } = this;
    Messenger.once(this.deleteFinishedEventName, this.stopWaitingDeleteConfirmation);

    const localKind = kind === undefined && broadcastStatus === BroadcastStatus.Live && hasSeriesScheduledRecording ? ScheduledRecordingsKind.Series : kind;

    this.setState({ isWaitingForDeleteConfirmation: true }, () =>
      localStopRecord({
        broadcastStatus,
        finishedOperationEventName,
        item,
        kind: localKind,
        previewCatchupScheduledEventId,
        programMetadata,
        seriesMetadata,
      }),
    );
  };

  renderStartRecordButton = (): React.Node => {
    const { context, isDisabled, kind, theme = Theme.Dark } = this.props;
    const { isWaitingForRecordConfirmation } = this.state;

    const recordKey = kind === ScheduledRecordingsKind.Series ? 'common.actions.record_series' : 'common.actions.record';

    return (
      <ButtonFX
        heightKind={context === ContextKind.Card ? HeightKind.Large : HeightKind.Medium}
        isDisabled={isDisabled}
        isLoading={isWaitingForRecordConfirmation}
        key='startRecording'
        onClick={this.handleStartRecordingButtonOnClick}
        theme={theme}
        widthKind={WidthKind.Stretched}
      >
        {Localizer.localize(recordKey)}
      </ButtonFX>
    );
  };

  renderStopRecordButton = (): React.Node => {
    const {
      context,
      isDisabled,
      item: { locType },
      kind,
      theme = Theme.Dark,
    } = this.props;
    const { isWaitingForDeleteConfirmation } = this.state;

    let deleteKey = 'common.actions.delete_recording';
    if (kind === ScheduledRecordingsKind.Series) {
      deleteKey = 'common.actions.delete_series_recording';
    } else if (locType === ItemLocationKind.Catchup) {
      deleteKey = 'tv.actions.delete_keep_from_replay';
    }

    return (
      <ButtonFX
        heightKind={context === ContextKind.Card ? HeightKind.Large : HeightKind.Medium}
        isDisabled={isDisabled}
        isLoading={isWaitingForDeleteConfirmation}
        key='stopRecording'
        onClick={this.handleStopRecordingButtonOnClick}
        theme={theme}
        widthKind={WidthKind.Stretched}
      >
        {Localizer.localize(deleteKey)}
      </ButtonFX>
    );
  };

  renderButton = (): React.Node => {
    const { actionType } = this.state;

    return actionType === RecordButtonActionKind.Record ? this.renderStartRecordButton() : this.renderStopRecordButton();
  };

  renderStartRecordPicto = (): React.Node => {
    const { isDisabled } = this.props;
    const { isWaitingForRecordConfirmation } = this.state;

    return (
      <PictoRecordingStart
        aria-label={Localizer.localize('accessibility.recordings.start')}
        className='recording'
        isDisabled={isDisabled || isWaitingForRecordConfirmation}
        key='startRecording'
        onClick={this.handleStartRecordingButtonOnClick}
      />
    );
  };

  renderStopRecordPicto = (): React.Node => {
    const {
      isDisabled,
      item: { locType },
      kind,
    } = this.props;
    const { hasScheduledRecording, hasSeriesScheduledRecording, isWaitingForDeleteConfirmation } = this.state;

    const isDisabledAggregated =
      isDisabled || isWaitingForDeleteConfirmation || (locType === ItemLocationKind.ScheduledEvent && kind === ScheduledRecordingsKind.Single && hasSeriesScheduledRecording && !hasScheduledRecording);

    return (
      <PictoRecordingStop
        aria-label={Localizer.localize('accessibility.recordings.stop')}
        className='recording'
        isDisabled={isDisabledAggregated}
        key='stopRecording'
        onClick={this.handleStopRecordingButtonOnClick}
      />
    );
  };

  renderPicto = (): React.Node => {
    const { actionType } = this.state;

    if (actionType === RecordButtonActionKind.None) {
      logError(`actionType: ${RecordButtonActionKind.None as string}`);
    }

    return actionType === RecordButtonActionKind.Record ? this.renderStartRecordPicto() : this.renderStopRecordPicto();
  };

  render(): React.Node {
    const { displayMode } = this.props;
    const { actionType } = this.state;

    if (actionType === RecordButtonActionKind.None) {
      return null;
    }

    return displayMode === DisplayMode.Button ? this.renderButton() : this.renderPicto();
  }
}

const mapStateToProps = (state: CombinedReducers): ReduxRecordButtonReducerStateType => {
  return {
    channels: state.appConfiguration.deviceChannels,
    commercialOffers: state.appConfiguration.rightsCommercialOffers,
    defaultRights: state.appConfiguration.rightsDefault,
    isDebugModeEnabled: state.appConfiguration.isDebugModeEnabled,
    isNpvrEnabled: state.appConfiguration.features[FEATURE_NPVR] && state.npvr.isEnabled,
    isRegisteredAsGuest: state.appRegistration.registration === RegistrationType.RegisteredAsGuest,
    isSubscriptionFeatureEnabled: state.appConfiguration.features[FEATURE_SUBSCRIPTION],
    npvrRecordingsFuture: state.npvr.npvrRecordingsFuture,
    npvrRecordingsList: state.npvr.npvrRecordingsList,
    npvrScheduledRecordingsList: state.npvr.npvrScheduledRecordingsList,
    userRights: state.appConfiguration.rightsUser,
  };
};

const mapDispatchToProps = (dispatch: Dispatch): ReduxRecordButtonDispatchToPropsType => {
  return {
    localHideModal: (): void => dispatch(hideModal()),
    localStartRecord: (recordItem: RECORD_ITEM_TYPE, isDebugModeEnabled: boolean): void => dispatch(startRecord(recordItem, isDebugModeEnabled)),
    localStopRecord: (recordItem: RECORD_ITEM_TYPE): void => dispatch(stopRecord(recordItem)),
  };
};

const RecordButton: React.ComponentType<RecordButtonPropType> = connect(mapStateToProps, mapDispatchToProps)(RecordButtonView);

export default RecordButton;
